import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  buildPipelineCountsFromBuildSummary,
  pipelineStatusDisplayOptions,
  type PipelineStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { formatRangeWindowNoDataText, type DashboardRange } from '@/features/dashboard/hooks';
import { buildPipelineMetricOptions } from '@/features/dashboard/sections/dashboardTablesOverviewMetricOptions';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
} from '@/features/metrics/components/statusMetricCards';
import { queryPipelineBuildSummary } from '@/features/pipelines/data';
import { buildPipelinesChartModelOverride } from '@/features/pipelines/hooks/pipelineChartSectionModel';
import {
  buildPreviousCustomWindow,
  buildPipelineMiniMetricChartDataFromSummary,
} from '@/features/pipelines/utils/buildMetricCharts';
import { buildPipelineStatusChartDataFromBuildSummary } from '@/features/pipelines/utils/buildStatusChart';
import { queryKeys } from '@/shared/hooks';
import { buildContainsFilter, buildInFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { MetricsStatusChartPanelDisplayModel } from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';
import type { MetricThresholdGroup } from '@flowqa/shared';
import type { BuildStatus } from '@flowqa/shared';

type UsePipelinesOverviewModelArgs = {
  accessToken: string | null;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  range: DashboardRange;
  rangeStartIso: string | null;
  rangeEndIso: string | null;
  selectedStartDay: string | null;
  selectedEndDay: string | null;
  previousRangeStartIso: string | null;
  previousRangeEndIso: string | null;
  debouncedSearchValue: string;
  selectedBuildStatuses: BuildStatus[];
  selectedTags: string[];
  hasOnlyNoBuildsSelected: boolean;
  hasUnsupportedChartStatusSelection: boolean;
  thresholds: MetricThresholdGroup;
  showDelta: boolean;
};

type UsePipelinesOverviewModelResult = {
  statusFilterOptions: Array<{ value: PipelineStatusKey; label: string }>;
  buildsNoRowsText: string;
  visiblePipelineMetricCards: MetricCardItem[];
  modelOverride: MetricsStatusChartPanelDisplayModel;
  currentSummaryLoading: boolean;
  hasCurrentSummary: boolean;
};

export const usePipelinesOverviewModel = ({
  accessToken,
  selectedProjectId,
  selectedOrganisationId,
  range,
  rangeStartIso,
  rangeEndIso,
  selectedStartDay,
  selectedEndDay,
  previousRangeStartIso,
  previousRangeEndIso,
  debouncedSearchValue,
  selectedBuildStatuses,
  selectedTags,
  hasOnlyNoBuildsSelected,
  hasUnsupportedChartStatusSelection,
  thresholds,
  showDelta,
}: UsePipelinesOverviewModelArgs): UsePipelinesOverviewModelResult => {
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);
  const statusFilterOptions = pipelineStatusDisplayOptions;
  const pipelineMetricOptions = useMemo(buildPipelineMetricOptions, []);
  const buildsNoRowsText = formatRangeWindowNoDataText('No builds found', range, hasCustomDates);
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const buildSummaryFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('pipelineName', debouncedSearchValue),
        buildInFilter('status', selectedBuildStatuses),
        tagFilters,
      ),
    [debouncedSearchValue, selectedBuildStatuses, tagFilters],
  );
  const currentBuildSummaryFiltersKey = useMemo(
    () =>
      JSON.stringify({
        latestOnly: isLatestMode,
        metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null,
        metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null,
        filters: buildSummaryFilters,
      }),
    [
      buildSummaryFilters,
      isLatestMode,
      rangeEndIso,
      rangeStartIso,
      selectedEndDay,
      selectedStartDay,
    ],
  );
  const currentPipelineBuildSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineBuildSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      currentBuildSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && !hasOnlyNoBuildsSelected,
    queryFn: async () =>
      queryPipelineBuildSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [],
          filters: buildSummaryFilters,
          metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? undefined,
          metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? undefined,
          latestOnly: isLatestMode,
        },
        accessToken,
      ),
  });
  const currentPipelineBuildSummary = currentPipelineBuildSummaryQuery.data ?? null;

  const previousChartWindow = useMemo(() => {
    if (isLatestMode || isAllMode) {
      return { startIso: null, endIso: null };
    }
    if (hasCustomDates) {
      return buildPreviousCustomWindow(selectedStartDay, selectedEndDay);
    }
    return { startIso: previousRangeStartIso, endIso: previousRangeEndIso };
  }, [
    hasCustomDates,
    isAllMode,
    isLatestMode,
    previousRangeEndIso,
    previousRangeStartIso,
    selectedEndDay,
    selectedStartDay,
  ]);
  const previousBuildSummaryFiltersKey = useMemo(
    () =>
      JSON.stringify({
        latestOnly: false,
        metricsStartDay: previousChartWindow.startIso?.slice(0, 10) ?? null,
        metricsEndDay: previousChartWindow.endIso?.slice(0, 10) ?? null,
        filters: buildSummaryFilters,
      }),
    [buildSummaryFilters, previousChartWindow.endIso, previousChartWindow.startIso],
  );
  const previousPipelineBuildSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineBuildSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousBuildSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && showDelta && !hasOnlyNoBuildsSelected,
    queryFn: async () =>
      queryPipelineBuildSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: 1,
          pageSize: 1,
          sorts: [],
          filters: buildSummaryFilters,
          metricsStartDay: previousChartWindow.startIso?.slice(0, 10) ?? undefined,
          metricsEndDay: previousChartWindow.endIso?.slice(0, 10) ?? undefined,
          latestOnly: false,
        },
        accessToken,
      ),
  });
  const previousPipelineBuildSummary = previousPipelineBuildSummaryQuery.data ?? null;
  const pipelineCounts = useMemo(
    () => buildPipelineCountsFromBuildSummary(currentPipelineBuildSummary),
    [currentPipelineBuildSummary],
  );
  const previousPipelineCounts = useMemo(
    () => buildPipelineCountsFromBuildSummary(previousPipelineBuildSummary),
    [previousPipelineBuildSummary],
  );
  const pipelineTotal = useMemo(
    () => currentPipelineBuildSummary?.buildTotal ?? 0,
    [currentPipelineBuildSummary?.buildTotal],
  );
  const previousPipelineTotal = useMemo(
    () => previousPipelineBuildSummary?.buildTotal ?? 0,
    [previousPipelineBuildSummary?.buildTotal],
  );
  const pipelineDeltas = useMemo(
    () => (showDelta ? buildStatusDeltaMap(pipelineCounts, previousPipelineCounts) : undefined),
    [pipelineCounts, previousPipelineCounts, showDelta],
  );
  const currentBuildMetricChartData = useMemo(
    () => buildPipelineMiniMetricChartDataFromSummary(currentPipelineBuildSummary),
    [currentPipelineBuildSummary],
  );
  const previousBuildMetricChartData = useMemo(
    () =>
      showDelta ? buildPipelineMiniMetricChartDataFromSummary(previousPipelineBuildSummary) : null,
    [previousPipelineBuildSummary, showDelta],
  );
  const summaryPassRate = currentBuildMetricChartData.passRate;
  const previousSummaryPassRate = showDelta
    ? (previousBuildMetricChartData?.passRate ?? null)
    : null;
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: null,
        previousMetrics: null,
        frequencyOverride: currentBuildMetricChartData.frequencyPerDay,
        previousFrequencyOverride: previousBuildMetricChartData?.frequencyPerDay ?? null,
        avgDurationOverride: currentBuildMetricChartData.avgDurationSeconds,
        previousAvgDurationOverride: previousBuildMetricChartData?.avgDurationSeconds ?? null,
        passRateOverride: summaryPassRate,
        previousPassRateOverride: previousSummaryPassRate,
        thresholds,
        showDelta,
      }),
    [
      currentBuildMetricChartData.avgDurationSeconds,
      currentBuildMetricChartData.frequencyPerDay,
      previousBuildMetricChartData?.avgDurationSeconds,
      previousBuildMetricChartData?.frequencyPerDay,
      previousSummaryPassRate,
      showDelta,
      summaryPassRate,
      thresholds,
    ],
  );
  const stackedChartData = useMemo(() => {
    if (hasOnlyNoBuildsSelected) return buildPipelineStatusChartDataFromBuildSummary(null, []);
    return buildPipelineStatusChartDataFromBuildSummary(
      currentPipelineBuildSummary,
      selectedBuildStatuses,
    );
  }, [currentPipelineBuildSummary, hasOnlyNoBuildsSelected, selectedBuildStatuses]);
  const passRateChartData = hasUnsupportedChartStatusSelection
    ? []
    : currentBuildMetricChartData.passRateChartData;
  const frequencyChartData = hasUnsupportedChartStatusSelection
    ? []
    : currentBuildMetricChartData.frequencyChartData;
  const durationChartData = hasUnsupportedChartStatusSelection
    ? []
    : currentBuildMetricChartData.durationChartData;
  const visiblePipelineMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: pipelineMetricOptions,
        counts: pipelineCounts,
        deltas: pipelineDeltas,
        total: pipelineTotal,
        previousCounts: previousPipelineCounts,
        previousTotal: previousPipelineTotal,
        loading: currentPipelineBuildSummaryQuery.isLoading,
        showDelta,
      }).filter((card) => card.id !== 'none'),
    [
      currentPipelineBuildSummaryQuery.isLoading,
      pipelineCounts,
      pipelineDeltas,
      pipelineMetricOptions,
      pipelineTotal,
      previousPipelineCounts,
      previousPipelineTotal,
      showDelta,
    ],
  );
  const modelOverride = useMemo(
    () =>
      buildPipelinesChartModelOverride({
        stackedChartData,
        passRateChartData,
        frequencyChartData,
        durationChartData,
        metricValueItems,
      }),
    [durationChartData, frequencyChartData, metricValueItems, passRateChartData, stackedChartData],
  );

  return {
    statusFilterOptions,
    buildsNoRowsText,
    visiblePipelineMetricCards,
    modelOverride,
    currentSummaryLoading: currentPipelineBuildSummaryQuery.isLoading,
    hasCurrentSummary: currentPipelineBuildSummary !== null,
  };
};
